// stores/auth.js - Version définitive pour Smart LabStock
import {
    defineStore
} from 'pinia'
import {
    ref,
    computed
} from 'vue'
import api from '@/services/apiClient.js'

export const useAuthStore = defineStore('auth', () => {
    // État
    const user = ref(null)
    const token = ref(localStorage.getItem('token') || null)
    const refreshToken = ref(localStorage.getItem('refreshToken') || null)
    const isLoading = ref(false)
    const error = ref(null)

    // Computed
    const isAuthenticated = computed(() => {
        return !!token.value && !!user.value
    })

    const userRole = computed(() => {
        return user.value ? user.value.role : null
    })

    const userLaboratory = computed(() => {
        return user.value ? user.value.laboratory : null
    })

    const userFullName = computed(() => {
        return user.value ? user.value.fullName : ''
    })

    const userInitials = computed(() => {
        return user.value ? user.value.initials : ''
    })

    // Vérifications de rôles
    const isAdmin = computed(() => userRole.value === 'ADMIN')
    const isPurchaseManager = computed(() => userRole.value === 'RESPONSABLE_ACHAT')
    const isLabManager = computed(() => userRole.value === 'RESPONSABLE_LABO')
    const isTechnician = computed(() => userRole.value === 'TECHNICIEN')
    const isControleGestion = computed(() => userRole.value === 'CONTROLEUR_GESTION')

    // Fonction de connexion
    const login = async (credentials) => {
        isLoading.value = true
        error.value = null

        try {
            const response = await api.post('/auth/login', credentials)

            if (response.status === 200 || response.status === 201) {
                const data = response.data

                // Stocker les tokens
                const tokens = data.data ? data.data.tokens : data.tokens || {}
                token.value = tokens.accessToken || tokens.token || data.token || data.accessToken
                refreshToken.value = tokens.refreshToken || data.refreshToken

                // Stocker les données utilisateur
                const userData = data.data ? data.data.user : data.user || data
                const firstName = userData.firstName || userData.first_name || ''
                const lastName = userData.lastName || userData.last_name || ''
                user.value = {
                    id: userData.id,
                    email: userData.email,
                    firstName: firstName,
                    lastName: lastName,
                    role: userData.role,
                    laboratory: userData.laboratory,
                    laboratoryId: userData.laboratoryId || (userData.laboratory && userData.laboratory.id) || null,
                    fullName: `${firstName} ${lastName}`.trim(),
                    initials: `${firstName.charAt(0) || '?'}${lastName.charAt(0) || '?'}`
                }

                // Sauvegarder dans localStorage
                localStorage.setItem('token', token.value)
                if (refreshToken.value) {
                    localStorage.setItem('refreshToken', refreshToken.value)
                }
                localStorage.setItem('user', JSON.stringify(user.value))

                return {
                    success: true,
                    user: user.value
                }
            } else {
                return {
                    success: false,
                    message: (response.data && response.data.message) || 'Erreur de connexion'
                }
            }
        } catch (err) {
            console.error('Erreur de connexion:', err)

            let message = 'Erreur de connexion au serveur'
            if (err.response && err.response.data) {
                message = err.response.data.message || err.response.data.error || message
            }

            error.value = message
            return {
                success: false,
                message: message
            }
        } finally {
            isLoading.value = false
        }
    }

    // Fonction de déconnexion
    const logout = async () => {
        try {
            if (token.value) {
                await api.post('/auth/logout')
            }
        } catch (error) {
            console.error('Erreur lors de la déconnexion:', error)
        } finally {
            // Nettoyer l'état local
            user.value = null
            token.value = null
            refreshToken.value = null

            // Nettoyer localStorage
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('user')
        }
    }

    // Initialiser le store
    const initializeStore = () => {
        const storedUser = localStorage.getItem('user')
        if (storedUser && token.value) {
            try {
                user.value = JSON.parse(storedUser)
            } catch (error) {
                console.error('Erreur lors du parsing des données utilisateur:', error)
                logout()
            }
        }
    }

    // Fonction pour vérifier la validité de la session
    const isSessionValid = () => {
        if (!token.value || !user.value) return false

        try {
            // Pour JWT, on peut vérifier l'expiration si le token est décodé
            // Pour l'instant, on vérifie juste la présence
            return true
        } catch (error) {
            console.error('Erreur lors de la vérification de session:', error)
            return false
        }
    }

    // Fonction pour vérifier si une route est autorisée
    const isRouteAllowed = (path) => {
        if (!user.value) return false

        // Logique de vérification des routes selon le rôle
        const roleRoutes = {
            'ADMIN': ['/admin', '/achat', '/labo', '/technicien', '/controle'],
            'RESPONSABLE_ACHAT': ['/achat'],
            'RESPONSABLE_LABO': ['/labo'],
            'CORRESPONDANT_ACHAT': ['/labo'],
            'TECHNICIEN': ['/technicien'],
            'CONTROLEUR_GESTION': ['/controle'],
            'RESPONSABLE_SERVICE': ['/service'],
            'DAF': ['/daf'],
            'COMPTABLE': ['/compta'],
            'DG': ['/dg']
        }

        const allowedPrefixes = roleRoutes[user.value.role] || []
        return allowedPrefixes.some(prefix => path.startsWith(prefix))
    }

    // Fonction pour vérifier si le token est expiré
    const isTokenExpired = () => {
        if (!token.value) return true

        try {
            // Décoder le JWT pour vérifier l'expiration
            const parts = token.value.split('.')
            if (parts.length !== 3) return true

            const payload = JSON.parse(atob(parts[1]))
            const expirationTime = payload.exp * 1000 // exp est en secondes, convertir en ms
            const currentTime = Date.now()

            // Considérer le token comme expiré s'il expire dans les 5 minutes
            return currentTime > (expirationTime - 5 * 60 * 1000)
        } catch (error) {
            console.error('Erreur lors de la vérification du token:', error)
            return true
        }
    }

    // Fonction pour renouveler la session
    const renewSession = async () => {
        try {
            // Ne pas tenter de renouveler si pas de refresh token
            if (!refreshToken.value) {
                return false
            }

            // Ne renouveler que si le token est expiré ou proche de l'expiration
            if (!isTokenExpired()) {
                return true
            }

            const response = await api.post('/auth/refresh', {
                refreshToken: refreshToken.value
            })

            if (response.status === 200 || response.status === 201) {
                const data = response.data
                const tokens = data.data ? data.data.tokens : data.tokens || {}

                // Mettre à jour les tokens
                token.value = tokens.accessToken || tokens.token || data.token || data.accessToken
                if (tokens.refreshToken) {
                    refreshToken.value = tokens.refreshToken
                }

                // Sauvegarder dans localStorage
                localStorage.setItem('token', token.value)
                if (refreshToken.value) {
                    localStorage.setItem('refreshToken', refreshToken.value)
                }

                return true
            }
            return false
        } catch (error) {
            console.error('Erreur lors du renouvellement de session:', error)
            // Ne pas déconnecter automatiquement - laisser l'utilisateur continuer
            // Le logout ne se fera que si une requête API échoue avec 401
            return false
        }
    }

    // Fonction pour obtenir le dashboard par défaut selon le rôle
    const getDefaultDashboard = () => {
        if (!user.value) return '/'

        switch (user.value.role) {
            case 'ADMIN':
                return '/admin/dashboard'
            case 'RESPONSABLE_ACHAT':
                return '/achat/dashboard'
            case 'RESPONSABLE_LABO':
                return '/labo/dashboard'
            case 'CORRESPONDANT_ACHAT':
                return '/labo/dashboard'
            case 'TECHNICIEN':
                return '/technicien/dashboard'
            case 'CONTROLEUR_GESTION':
                return '/controle/dashboard'
            case 'RESPONSABLE_SERVICE':
                return '/service/dashboard'
            case 'DAF':
                return '/daf/dashboard'
            case 'COMPTABLE':
                return '/compta/dashboard'
            case 'DG':
                return '/dg/dashboard'
            default:
                return '/'
        }
    }

    // Fonctions utilitaires pour les rôles
    const getRoleColors = (role) => {
        const colors = {
            'ADMIN': {
                bg: 'bg-red-500',
                text: 'text-red-600'
            },
            'RESPONSABLE_ACHAT': {
                bg: 'bg-blue-500',
                text: 'text-blue-600'
            },
            'RESPONSABLE_LABO': {
                bg: 'bg-green-500',
                text: 'text-green-600'
            },
            'CORRESPONDANT_ACHAT': {
                bg: 'bg-emerald-500',
                text: 'text-emerald-600'
            },
            'TECHNICIEN': {
                bg: 'bg-purple-500',
                text: 'text-purple-600'
            },
            'CONTROLEUR_GESTION': {
                bg: 'bg-teal-500',
                text: 'text-teal-600'
            },
            'RESPONSABLE_SERVICE': {
                bg: 'bg-indigo-500',
                text: 'text-indigo-600'
            },
            'DAF': {
                bg: 'bg-amber-500',
                text: 'text-amber-600'
            },
            'COMPTABLE': {
                bg: 'bg-violet-500',
                text: 'text-violet-600'
            },
            'DG': {
                bg: 'bg-rose-500',
                text: 'text-rose-600'
            }
        }
        return colors[role] || {
            bg: 'bg-gray-500',
            text: 'text-gray-600'
        }
    }

    const getRoleLabel = (role) => {
        const labels = {
            'ADMIN': 'Administrateur',
            'RESPONSABLE_ACHAT': 'Responsable Achat',
            'RESPONSABLE_LABO': 'Responsable Labo',
            'CORRESPONDANT_ACHAT': 'Correspondant Achat',
            'TECHNICIEN': 'Technicien',
            'CONTROLEUR_GESTION': 'Contrôleur de Gestion',
            'RESPONSABLE_SERVICE': 'Responsable de Service',
            'DAF': 'Directeur Administratif et Financier',
            'COMPTABLE': 'Comptable',
            'DG': 'Directeur Général'
        }
        return labels[role] || 'Utilisateur'
    }

    return {
        // État
        user,
        token,
        refreshToken,
        isLoading,
        error,

        // Computed
        isAuthenticated,
        userRole,
        userLaboratory,
        userFullName,
        userInitials,
        isAdmin,
        isPurchaseManager,
        isLabManager,
        isTechnician,
        isControleGestion,

        // Actions
        login,
        logout,
        initializeStore,
        getDefaultDashboard,
        isSessionValid,
        isRouteAllowed,
        isTokenExpired,
        renewSession,
        getRoleColors,
        getRoleLabel
    }
})